import { createClient } from '@connectrpc/connect';
import { FamilyService } from './gen/family_service_pb';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { getGrpcTransport } from './transport';
import {
  normalizeRequestedParameterIntents,
  type RequestedParameterIntentInput,
} from './requestedParameterIntent';
import { normalizeRevitDataType } from '../revit/revitDataType';
import type { RevitElementCategory, RevitFamily } from '../revit/types';
import { RevitOperationAcceptanceTimeoutMs } from '../../lib/revit/revitOperationTimeoutPolicy';

export interface FamilySnapshotGrpcResult {
  success: boolean;
  families: RevitFamily[];
  error?: string;
}

export interface FamilySnapshotGrpcOptions {
  scopedFamilyIds?: string[];
  requestedParameters?: readonly RequestedParameterIntentInput[];
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

function mapResponse(response: any): FamilySnapshotGrpcResult {
  return {
    success: Boolean(response.isSuccess),
    families: Array.isArray(response.families)
      ? response.families.map((family: any): RevitFamily => ({
          id: family.id,
          name: family.name,
          category: family.category as RevitElementCategory,
          isEditable: Boolean(family.isEditable),
          isSystemFamily: Boolean(family.isSystemFamily),
          parameters: [],
          types: Array.isArray(family.types)
            ? family.types.map((familyType: any) => ({
                id: familyType.id,
                name: familyType.name,
                parameterValues: Array.isArray(familyType.parameterValues)
                  ? familyType.parameterValues.map((parameter: any) => ({
                      parameterId: parameter.parameterId,
                      parameterName: parameter.parameterName,
                      value: parameter.value,
                      isReadOnly: Boolean(parameter.isReadOnly),
                      isInstance: Boolean(parameter.isInstance),
                      dataType: normalizeRevitDataType(parameter.dataType),
                    }))
                  : [],
              }))
            : [],
        }))
      : [],
    error: response.error || undefined,
  };
}

function buildFamilySnapshotRequest(
  parameterNames: string[],
  options: FamilySnapshotGrpcOptions
) {
  return {
    parameterNames,
    scopedFamilyIds: options.scopedFamilyIds ?? [],
    requestedParameters: normalizeRequestedParameterIntents(options.requestedParameters),
  };
}

export const __familySnapshotClientTestInternals = { buildFamilySnapshotRequest };

export async function getFamilySnapshotGrpc(
  parameterNames: string[],
  options: FamilySnapshotGrpcOptions
): Promise<FamilySnapshotGrpcResult> {
  const transport = getGrpcTransport();
  if (!transport) return { success: false, families: [], error: 'gRPC transport is unavailable' };
  const client = createClient(FamilyService, transport);
  const request = buildFamilySnapshotRequest(parameterNames, options);
  try {
    if (options.onProgress) {
      const start = await client.startGetFamiliesWithParameters(request);
      if (!start.isSuccess || !start.operationId) {
        return { success: false, families: [], error: start.error || 'Family snapshot did not start' };
      }
      const operation = await pollRevitOperation(start.operationId, {
        maxRevitWaitMs: RevitOperationAcceptanceTimeoutMs,
        onProgress: options.onProgress,
        parseResult: (json) => mapResponse(JSON.parse(json)),
      });
      return operation.result ?? {
        success: false,
        families: [],
        error: operation.error || 'Family snapshot operation failed',
      };
    }
    return mapResponse(await client.getFamiliesWithParameters(request));
  } catch (error) {
    return {
      success: false,
      families: [],
      error: error instanceof Error ? error.message : 'Family snapshot request failed',
    };
  }
}
